import { type MouseEvent } from 'react';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { type Scopes, type User } from '../../auth';
import { ghostingEnabled, requestGhostingCookie, url } from '../../gonfalon';
import { type Settings } from '../../settings';
import { DataList, DataListItem, Module, ModuleContent, ModuleHeader } from '../../shared';
import {
  linkToCatfood,
  linkToDataDogLogs,
  linkToHex,
  linkToHoneycombEvents,
  linkToHoneycombSDKVersions,
  linkToHowToGhostConfluencePage,
  linkToLooker,
  linkToSalesforce,
  linkToSessionReplay,
  linkToStripe,
} from '../../shared/third-party';
import { type Account, type Subscription } from '../model';

import { OrbLink } from './OrbLink';

type LinkHubProps = Readonly<{
  user: User;
  settings: Settings;
  scopes: Scopes;
  subscription?: Subscription;
  account: Account;
}>;

const handleGhost = (accountID: string, email: string) => (e: MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();

  const win = window.open('', '_blank');
  requestGhostingCookie(accountID, email).then(() => {
    if (win) {
      win.location.href = url;
    }
  });
};

export const LinkHub = ({ user, settings, scopes, subscription, account }: LinkHubProps) => {
  const accountID = account._id;

  return (
    <Module>
      <ModuleHeader>Link Hub</ModuleHeader>
      <ModuleContent snug>
        <DataList>
          {scopes.ghostingLink && ghostingEnabled && (
            <DataListItem>
              {/* oxlint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- swapping this to a button is a UX change, out of scope here */}
              <a onClick={handleGhost(accountID, user.email || 'anon')}>👀 Access account</a>
              <TooltipTrigger>
                <Focusable>
                  <a
                    href={linkToHowToGhostConfluencePage()}
                    className="howToGhost-Link"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: '8px' }}
                  >
                    (How?)
                  </a>
                </Focusable>
                <Tooltip placement="top" variant="popover">
                  <div>
                    <p>Ensure this customer has enabled enhanced support on the security tab of their LD instance</p>
                    <p>Once enabled, click the link to access the account in read-only mode</p>
                  </div>
                </Tooltip>
              </TooltipTrigger>
            </DataListItem>
          )}

          <DataListItem>
            <a href={linkToCatfood(accountID)} target="_blank" rel="noopener noreferrer">
              Catfood
            </a>
          </DataListItem>

          <DataListItem>
            <a href={linkToDataDogLogs(accountID)} target="_blank" rel="noopener noreferrer">
              Datadog
            </a>
          </DataListItem>

          <DataListItem>
            <a href={linkToSessionReplay(accountID)} target="_blank" rel="noopener noreferrer">
              Session Replay
            </a>
          </DataListItem>

          {settings.enableHoneycombLinks && (
            <>
              <DataListItem>
                <a href={linkToHoneycombEvents(accountID)} target="_blank" rel="noopener noreferrer">
                  Events - Honeycomb
                </a>
              </DataListItem>
              <DataListItem>
                <a href={linkToHoneycombSDKVersions(accountID)} target="_blank" rel="noopener noreferrer">
                  SDK Versions - Honeycomb
                </a>
              </DataListItem>
            </>
          )}

          <DataListItem>
            <a href={linkToLooker(accountID)} target="_blank" rel="noopener noreferrer">
              Looker
            </a>
          </DataListItem>

          <DataListItem>
            <a href={linkToHex(accountID)} target="_blank" rel="noopener noreferrer">
              Hex
            </a>
          </DataListItem>

          <DataListItem>
            <a href={linkToSalesforce(accountID)} target="_blank" rel="noopener noreferrer">
              Salesforce
            </a>
          </DataListItem>

          <DataListItem>
            <a href={linkToStripe(accountID)} target="_blank" rel="noopener noreferrer">
              Stripe
            </a>
          </DataListItem>

          {subscription?.orbSubscriptionId && (
            <DataListItem>
              <OrbLink orbSubscriptionId={subscription.orbSubscriptionId} account={account}>
                Orb
              </OrbLink>
            </DataListItem>
          )}
        </DataList>
      </ModuleContent>
    </Module>
  );
};
